import { gql, DocumentNode } from '@apollo/client';
import { Box, Typography } from '@mui/material';
import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { Button } from '../../../Button';
import MuiModal, { MuiModalProps } from '../../../../mui/MuiModal/MuiModal';
import styles from './InvoiceViewPaymentInstallmentRefundModal.module.scss';
import rpcShared from '@rockpapercoin/rpc-shared';
import {
	useInvoiceViewPaymentInstallmentRefundModal_RefundInstallmentMutation,
	useInvoiceViewPaymentInstallmentRefundModal_RefundOfflinePaymentMutation,
	useInvoiceViewPaymentInstallmentRefundModal_UpdateRefundedInstallmentMutation,
	InstallmentStatus,
	PaymentProcessor,
	useInvoiceViewPaymentInstallmentRefundModal_UnrecordOfflinePaymentOrRefundMutation,
	RecordOfflinePaymentOrRefundType,
	InvoiceViewPaymentInstallmentRefundModalPaymentInstallmentFragment,
	InvoiceViewPaymentInstallmentRefundModalInvoiceFragment,
} from '#types';
import { InvoiceViewPaymentMethod } from '../InvoiceViewPaymentMethod';
import MuiCurrencyInput from '../../../../mui/MuiCurrencyInput/MuiCurrencyInput';
import { DatePicker } from '../../../DatePicker';
import dayjs from 'dayjs';
import MuiTextArea from '../../../../mui/MuiTextArea/MuiTextArea';
import { useToast } from '../../../../hooks/useToast';
import { InvoiceViewPaymentInstallmentStatus } from '../InvoiceViewPaymentInstallmentStatus';

export const invoiceViewPaymentInstallmentRefundModalPaymentInstallmentFragment = gql`
	fragment invoiceViewPaymentInstallmentRefundModalPaymentInstallment on PaymentInstallment {
		id
		name
		amountDue
		status
		dueDate
		paymentDate
		processor
		amountRefunded
		refundDate
		refundNote
		...invoiceViewPaymentMethodPaymentInstallment
		...invoiceViewPaymentInstallmentStatus
	}
	${ InvoiceViewPaymentMethod.fragments
		.invoiceViewPaymentMethodPaymentInstallmentFragment }
	${ InvoiceViewPaymentInstallmentStatus.fragments
		.invoiceViewPaymentInstallmentStatusFragment }
`;

export const invoiceViewPaymentInstallmentRefundModalInvoiceFragment = gql`
	fragment invoiceViewPaymentInstallmentRefundModalInvoice on Invoice {
		id
		discountAmount
		lineItems {
			id
			name
			taxPercentage
			rate
			quantity
		}
		paymentInstallments {
			...invoiceViewPaymentInstallmentRefundModalPaymentInstallment
		}
		contact {
			...getViewingUserDocumentRelationshipContact
		}
		organization {
			id
			country
		}
	}
	${ invoiceViewPaymentInstallmentRefundModalPaymentInstallmentFragment }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const invoiceViewPaymentInstallmentRefundModal_refundInstallment = gql`
	mutation invoiceViewPaymentInstallmentRefundModal_refundInstallment(
		$data: RefundInstallmentInput!
	) {
		refundInstallment(data: $data) {
			id
			status
			paymentInstallments {
				...invoiceViewPaymentInstallmentRefundModalPaymentInstallment
			}
		}
	}
	${ invoiceViewPaymentInstallmentRefundModalPaymentInstallmentFragment }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const invoiceViewPaymentInstallmentRefundModal_updateRefundedInstallment = gql`
	mutation invoiceViewPaymentInstallmentRefundModal_updateRefundedInstallment(
		$data: UpdateRefundedInstallmentInput!
		$where: PaymentInstallmentWhereUniqueInput!
	) {
		updateRefundedInstallment(data: $data, where: $where) {
			id
			status
			paymentInstallments {
				...invoiceViewPaymentInstallmentRefundModalPaymentInstallment
			}
		}
	}
	${ invoiceViewPaymentInstallmentRefundModalPaymentInstallmentFragment }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const invoiceViewPaymentInstallmentRefundModal_refundOfflinePayment = gql`
	mutation invoiceViewPaymentInstallmentRefundModal_refundOfflinePayment(
		$data: RecordOfflineRefundInput!
		$where: PaymentInstallmentWhereUniqueInput!
	) {
		refundOfflinePayment(data: $data, where: $where) {
			id
			status
			paymentInstallments {
				...invoiceViewPaymentInstallmentRefundModalPaymentInstallment
			}
		}
	}
	${ invoiceViewPaymentInstallmentRefundModalPaymentInstallmentFragment }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const invoiceViewPaymentInstallmentRefundModal_unrecordOfflinePaymentOrRefund = gql`
	mutation invoiceViewPaymentInstallmentRefundModal_unrecordOfflinePaymentOrRefund(
		$data: RecordOfflinePaymentOrRefundInput!
		$where: PaymentInstallmentWhereUniqueInput!
	) {
		unrecordOfflinePaymentOrRefund(data: $data, where: $where) {
			id
			status
			isPastDue
		}
	}
`;

const ModalValue: React.FC<{
	className?: string;
	label: string;
	value: string | React.JSX.Element;
	isBold?: true;
	isVisible?: boolean;
}> = ( { className, label, value, isBold, isVisible = true } ) =>
	isVisible ? (
		<Box className={ className }>
			<Typography color='gray' className={ styles.label }>
				{ label }
			</Typography>
			{ typeof value === 'string' ? (
				<Typography
					className={ isBold ? styles.bold : undefined }
					data-cy={ label.replace( ' ', '-' ).toLowerCase() }
				>
					{ value }
				</Typography>
			) : (
				value
			) }
		</Box>
	) : null;

type HandleOnCloseProps = Parameters<
	NonNullable<MuiModalProps['muiModalProps']['onClose']>
>;

export type InvoiceViewPaymentInstallmentRefundModalProps = {
	requestingUserBaseId?: string;
	paymentInstallment?: InvoiceViewPaymentInstallmentRefundModalPaymentInstallmentFragment;
	invoice?: InvoiceViewPaymentInstallmentRefundModalInvoiceFragment;
} & Pick<MuiModalProps['muiModalProps'], 'onClose'>;

type InvoiceViewPaymentInstallmentRefundModalPropsWithFragment =
	React.FC<InvoiceViewPaymentInstallmentRefundModalProps> & {
		fragments: {
			invoiceViewPaymentInstallmentRefundModalPaymentInstallmentFragment: DocumentNode;
		};
	};

export const InvoiceViewPaymentInstallmentRefundModal: InvoiceViewPaymentInstallmentRefundModalPropsWithFragment =
	( { paymentInstallment, invoice, requestingUserBaseId, onClose } ) => {
		const { showError, showSuccess } = useToast();
		const [ amountRefunded, setAmountRefunded ] = useState(
			paymentInstallment?.amountRefunded || 0
		);
		const [ validateRefundAmount, setValidateRefundAmount ] = useState<
			string | undefined
		>();
		const [ refundDate, setRefundDate ] = useState<string | Date | undefined>(
			paymentInstallment?.refundDate || new Date()
		);
		const [ validateRefundDate, setValidateRefundDate ] = useState<
			string | undefined
		>();
		const [ refundNote, setRefundNote ] = useState(
			paymentInstallment?.refundNote || ''
		);
		useEffect( () => {
			setAmountRefunded( ( prevState ) =>
				paymentInstallment?.amountRefunded
					? paymentInstallment.amountRefunded
					: prevState
			);
			setRefundDate( paymentInstallment?.refundDate || new Date() );
			setRefundNote( ( prevState ) =>
				paymentInstallment?.refundNote
					? paymentInstallment.refundNote
					: prevState
			);
		}, [ paymentInstallment ] );
		const blurValidationEnabledRef = useRef( false );
		const handleOnClose = useCallback(
			( event: HandleOnCloseProps[0], reason: HandleOnCloseProps[1] ) => {
				blurValidationEnabledRef.current = false;
				setAmountRefunded( 0 );
				setRefundDate( undefined );
				setRefundNote( '' );
				onClose?.( event, reason );
			},
			[ onClose ]
		);
		const [ refundInstallment, { loading: refundStripeLoading } ] =
			useInvoiceViewPaymentInstallmentRefundModal_RefundInstallmentMutation();
		const [ updateRefundedInstallment, { loading: updateRefundedStripeLoading }, ] =
			useInvoiceViewPaymentInstallmentRefundModal_UpdateRefundedInstallmentMutation();
		const [ refundOfflinePayment, { loading: refundOfflineLoading } ] =
			useInvoiceViewPaymentInstallmentRefundModal_RefundOfflinePaymentMutation();
		const [ unrecordOfflinePaymentOrRefund, { loading: unrecordOfflinePaymentOrRefundLoading }, ] =
			useInvoiceViewPaymentInstallmentRefundModal_UnrecordOfflinePaymentOrRefundMutation();

		const refund = useCallback(
			async ( id: string ) => {
				if ( paymentInstallment?.processor === PaymentProcessor.Stripe ) {
					if ( !paymentInstallment?.refundDate ) {
						return await refundInstallment( {
							variables: {
								data: {
									amount: amountRefunded,
									installmentID: id,
									refundNote,
								},
							},
						} );
					} else {
						return await updateRefundedInstallment( {
							variables: {
								where: { id },
								data: {
									refundNote,
								},
							},
						} );
					}
				} else {
					return await refundOfflinePayment( {
						variables: {
							where: { id },
							data: {
								amountRefunded,
								// this silly default is just to make typescript happy. Validation should prevent using them
								refundDate: refundDate || new Date(),
								refundNote,
							},
						},
					} );
				}
			},
			[
				amountRefunded,
				paymentInstallment?.processor,
				paymentInstallment?.refundDate,
				refundDate,
				refundInstallment,
				refundNote,
				refundOfflinePayment,
				updateRefundedInstallment,
			]
		);
		const paymentInstallmentValues =
			invoice && paymentInstallment
				? rpcShared.invoices.calculateInstallment( invoice, paymentInstallment )
				: undefined;
		/* amountDue does not include fees but amountPaid does, so we need to calculate here what is
		actually refundable, which does not include fees */
		const maximumRefundAmount = paymentInstallmentValues
			? paymentInstallmentValues.status === InstallmentStatus.Unpaid
				? 0
				: paymentInstallmentValues.amountDue +
				  paymentInstallmentValues.gratuityPaid -
				  paymentInstallmentValues.amountRefunded
			: 0;
		// offline payments can have any date in the past, but stripe must have a date of "now"
		const lowerBoundDate = useMemo(
			() =>
				paymentInstallment?.paymentType
					? paymentInstallment?.paymentDate
					: new Date(),
			[ paymentInstallment?.paymentType, paymentInstallment?.paymentDate ]
		);
		const upperBoundDate = useMemo( () => new Date(), [] );
		const dateInputDisabled = !paymentInstallment?.paymentType;
		const validateDate = useCallback(
			( refundDateOverride?: string | Date ) => {
				const blurValidationEnabled = blurValidationEnabledRef.current;
				const calculatedRefundDate = refundDateOverride || refundDate;
				if ( dateInputDisabled ) {
					// if the date field is disabled, don't worry about this client-side validation
					if ( blurValidationEnabled ) {
						setValidateRefundDate( undefined );
					}
					return true;
				} else if ( calculatedRefundDate ) {
					if (
						dayjs( calculatedRefundDate ).isBefore( dayjs( lowerBoundDate ), 'day' )
					) {
						if ( blurValidationEnabled ) {
							setValidateRefundDate( 'Refund date is too far in the past' );
						}
						return false;
					} else if (
						dayjs( calculatedRefundDate ).isAfter( dayjs( upperBoundDate ), 'day' )
					) {
						if ( blurValidationEnabled ) {
							setValidateRefundDate( 'Refund date must be after payment date.' );
						}
						return false;
					} else {
						if ( blurValidationEnabled ) {
							setValidateRefundDate( undefined );
						}
						return true;
					}
				} else {
					if ( blurValidationEnabled ) {
						setValidateRefundDate( 'No refund date provided' );
					}
					return false;
				}
			},
			[
				lowerBoundDate,
				refundDate,
				upperBoundDate,
				dateInputDisabled
			]
		);
		const amountInputDisabled =
			paymentInstallment?.processor === PaymentProcessor.Stripe &&
			!!paymentInstallment?.refundDate;
		const validateAmount = useCallback(
			( refundAmountOverride?: number ) => {
				const blurValidationEnabled = blurValidationEnabledRef.current;
				const calculatedRefundAmount = refundAmountOverride || amountRefunded;
				if ( amountInputDisabled ) {
					// if the amount field is disabled, don't worry about this client-side validation
					if ( blurValidationEnabled ) {
						setValidateRefundAmount( undefined );
					}
					return true;
				} else if ( calculatedRefundAmount ) {
					if (
						maximumRefundAmount &&
						calculatedRefundAmount > maximumRefundAmount &&
						paymentInstallment?.amountRefunded !== calculatedRefundAmount
					) {
						if ( blurValidationEnabled ) {
							setValidateRefundAmount(
								'Refund amount cannot exceed the payment amount'
							);
						}
						return false;
					} else {
						if ( blurValidationEnabled ) {
							setValidateRefundAmount( undefined );
						}
						return true;
					}
				} else {
					if ( blurValidationEnabled ) {
						setValidateRefundAmount( 'No refund amount provided' );
					}
					return false;
				}
			},
			[
				amountRefunded,
				maximumRefundAmount,
				paymentInstallment,
				amountInputDisabled,
			]
		);
		const validate = useCallback(
			( overrides?: { refundAmount?: number; refundDate?: Date | string } ) => {
				const dateValidation = validateDate( overrides?.refundDate );
				const amountValidation = validateAmount( overrides?.refundAmount );
				return dateValidation && amountValidation;
			},
			[ validateAmount, validateDate ]
		);

		const onUnrecordRefund = useCallback(
			async (
				installment: InvoiceViewPaymentInstallmentRefundModalPaymentInstallmentFragment,
				e: React.MouseEvent<HTMLButtonElement>
			) => {
				const res = await unrecordOfflinePaymentOrRefund( {
					variables: {
						where: { id: installment.id },
						data: {
							type: RecordOfflinePaymentOrRefundType.Refund,
						},
					},
					refetchQueries: [ 'invoiceViewInvoice_getInvoiceWhere', 'invoiceViewInvoice_getInvoiceHistory', ],
				} );
				if ( res.errors ) {
					res.errors.forEach( ( error ) => showError( error ) );
				} else {
					showSuccess( 'Refund unrecorded.' );
					handleOnClose( e, 'fromCode' );
				}
			},
			[
				unrecordOfflinePaymentOrRefund,
				showError,
				showSuccess,
				handleOnClose
			]
		);

		const { viewingUserIsCustomer, viewingUserIsAPPA } =
			rpcShared.contacts.getViewingUserDocumentRelationship( {
				contact: invoice?.contact,
				viewingUserId: requestingUserBaseId,
			} );
		const viewingUserIsCustomerOrAPPA =
			viewingUserIsCustomer || viewingUserIsAPPA;

		if (
			!invoice ||
			!paymentInstallment ||
			!paymentInstallmentValues ||
			!paymentInstallment.amountDue
		) {
			return null;
		}

		const currencyLabel = rpcShared.invoices.getCurrencyLabel(
			invoice?.organization?.country
		);

		return (
			<MuiModal
				muiModalProps={ {
					open: !!paymentInstallment,
					onClose: handleOnClose,
					className: styles.container,
				} }
				title={
					paymentInstallment.refundDate ? 'Update refund' : 'Refund payment'
				}
				controls={
					<Box className={ styles.buttonCopy }>
						{ paymentInstallment.processor === PaymentProcessor.Offline ? (
							<Typography>
								For payments received outside of Rock Paper Coin, returning
								payment funds is your responsibility.
							</Typography>
						) : null }
						<Box className={ styles.buttonContainer }>
							{ ( paymentInstallment.status === InstallmentStatus.Refunded ||
								paymentInstallment.status ===
									InstallmentStatus.FullyPaidPartiallyRefunded ) &&
							paymentInstallment.processor === PaymentProcessor.Offline ? (
									<Button
										data-cy='submit-unrecord-refund-button'
										variant='outlined'
										onClick={ async ( e ) => {
											await onUnrecordRefund( paymentInstallment, e );
										} }
										loading={
											refundStripeLoading ||
										refundOfflineLoading ||
										updateRefundedStripeLoading ||
										unrecordOfflinePaymentOrRefundLoading
										}
									>
									Unrecord
									</Button>
								) : null }
							<Button
								data-cy='submit-refund-button'
								onClick={ async ( e ) => {
									blurValidationEnabledRef.current = true;
									if ( validate() ) {
										const response = await refund( paymentInstallment.id );
										if ( response.errors ) {
											response.errors.forEach( ( error ) => showError( error ) );
										} else {
											handleOnClose( e, 'fromCode' );
											showSuccess(
												`Refund successfully ${
													paymentInstallment?.refundDate
														? 'updated'
														: 'submitted'
												}`
											);
										}
									}
								} }
								loading={
									refundStripeLoading ||
									refundOfflineLoading ||
									updateRefundedStripeLoading ||
									unrecordOfflinePaymentOrRefundLoading
								}
							>
								Save
							</Button>
						</Box>
					</Box>
				}
			>
				<Box className={ styles.columns }>
					<ModalValue
						label='Name'
						value={ paymentInstallment.name || '' }
						isBold
					/>
					<ModalValue
						label='Payment method'
						value={
							<InvoiceViewPaymentMethod
								paymentInstallment={ paymentInstallment }
							/>
						}
					/>
					<ModalValue
						label={
							paymentInstallment.status === InstallmentStatus.Unpaid
								? `Amount due${ currencyLabel }`
								: `Refundable amount${ currencyLabel }`
						}
						value={ rpcShared.invoices.numberToCurrency( maximumRefundAmount ) }
						isBold
					/>
					<ModalValue
						label='Status'
						value={
							<InvoiceViewPaymentInstallmentStatus
								installment={ paymentInstallment }
								isCustomerOrAPPA={ viewingUserIsCustomerOrAPPA }
							/>
						}
					/>
				</Box>
				<Box className={ styles.rows }>
					<MuiCurrencyInput
						fullWidth
						variant='standard'
						label={ `Refund amount${ currencyLabel }` }
						value={ amountRefunded }
						onChange={ ( value ) => {
							setAmountRefunded( value );
							// gotta pass in the value here because there's a useEffect delay between setting and reading is seems
							validate( { refundAmount: value } );
						} }
						slotProps={ { htmlInput: { 'data-cy': 'refund-amount' } } }
						disabled={ amountInputDisabled }
						errorText={ validateRefundAmount }
					/>
					<DatePicker
						label='Date refunded'
						value={ refundDate }
						onChange={ ( value ) => {
							setRefundDate( value || undefined );
							// gotta pass in the value here because there's a useEffect delay between setting and reading is seems
							validate( { refundDate: value || undefined } );
						} }
						slotProps={ {
							textField: {
								slotProps: { htmlInput: { 'data-cy': 'refund-date' } },
								variant: 'standard',
							},
						} }
						minDate={ lowerBoundDate ? dayjs( lowerBoundDate ) : undefined }
						maxDate={ upperBoundDate ? dayjs( upperBoundDate ) : undefined }
						disabled={ dateInputDisabled }
						clearable={ false }
						errorText={ validateRefundDate }
					/>
					<MuiTextArea
						fullWidth
						className={ styles.notes }
						label='Refund note (optional)'
						value={ refundNote }
						onChange={ ( e ) => setRefundNote( e.target.value ) }
						slotProps={ { htmlInput: { 'data-cy': 'refund-note' } } }
						maxLength={ 500 }
						countCharacters
					/>
				</Box>
			</MuiModal>
		);
	};

InvoiceViewPaymentInstallmentRefundModal.fragments = {
	invoiceViewPaymentInstallmentRefundModalPaymentInstallmentFragment,
};
